import { useToast } from '@/app/composables/useToast';
import { useProjectsStore } from '@/features/collaboration/projects/projects.store';
import { useI18n } from '@n8n/i18n';
import { ref } from 'vue';
import { createResultError, createResultOk, type Result } from '@n8n/utils/result';
import {
	NodeHelpers,
	type CredentialInformation,
	type GenericValue,
	type ICredentialDataDecryptedObject,
	type ICredentialType,
	type INodeProperties,
} from 'n8n-workflow';

import { useCredentialsStore } from '../credentials.store';
import type { ICredentialsResponse } from '../credentials.types';
import { useTelemetry } from '@/app/composables/useTelemetry';
import { useWorkflowsStore } from '@/app/stores/workflows.store';
import { useRootStore } from '@n8n/stores/useRootStore';
import { getTrustedOAuthOrigins, parseOAuthCallbackMessage } from './oauthCallback';

/**
 * Composable for OAuth credential type detection and authorization.
 * Used by NodeCredentials for the quick connect OAuth flow.
 */
export function useCredentialOAuth() {
	const credentialsStore = useCredentialsStore();
	const projectsStore = useProjectsStore();
	const workflowsStore = useWorkflowsStore();
	const rootStore = useRootStore();

	const toast = useToast();
	const i18n = useI18n();
	const telemetry = useTelemetry();

	const oauthAbortController = ref<AbortController | null>(null);
	const pendingCredentialId = ref<string | null>(null);

	/**
	 * Get parent types for a credential type (e.g., googleSheetsOAuth2Api extends googleOAuth2Api extends oAuth2Api).
	 */
	function getParentTypes(credentialTypeName: string, visited = new Set<string>()): string[] {
		if (visited.has(credentialTypeName)) return [];
		visited.add(credentialTypeName);

		const type = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (type?.extends === undefined) return [];

		const types: string[] = [];
		for (const typeName of type.extends) {
			types.push(typeName);
			types.push(...getParentTypes(typeName, visited));
		}
		return types;
	}

	/**
	 * Check if a credential type is an OAuth type (extends oAuth2Api or oAuth1Api).
	 */
	function isOAuthCredentialType(credentialTypeName: string): boolean {
		const parentTypes = getParentTypes(credentialTypeName);
		return (
			credentialTypeName === 'oAuth2Api' ||
			credentialTypeName === 'oAuth1Api' ||
			parentTypes.includes('oAuth2Api') ||
			parentTypes.includes('oAuth1Api')
		);
	}

	/**
	 * Check if a credential type is Google OAuth (extends googleOAuth2Api).
	 */
	function isGoogleOAuthType(credentialTypeName: string): boolean {
		const parentTypes = getParentTypes(credentialTypeName);
		return credentialTypeName === 'googleOAuth2Api' || parentTypes.includes('googleOAuth2Api');
	}

	/**
	 * Check if an OAuth credential type has all required fields managed/overwritten.
	 * This indicates the credential can be used with quick connect (just OAuth flow, no manual config).
	 * Reuses logic patterns from CredentialEdit.vue (credentialProperties + requiredPropertiesFilled).
	 */
	function canOAuthCredentialQuickConnect(credentialTypeName: string): boolean {
		if (!isOAuthCredentialType(credentialTypeName)) {
			return false;
		}

		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) {
			return false;
		}

		if (credentialType.__skipManagedCreation) {
			return false;
		}

		const overwrittenProperties = credentialType.__overwrittenProperties ?? [];
		const nonOverwrittenConfigurableProperties = getManuallyConfigurableProperties(
			credentialType,
		).filter((prop) => !overwrittenProperties.includes(prop.name));

		if (nonOverwrittenConfigurableProperties.length === 0) {
			return true;
		}

		if (overwrittenProperties.length === 0) {
			return false;
		}

		return nonOverwrittenConfigurableProperties.every(
			(prop) => prop.required !== true || (prop.type !== 'string' && prop.type !== 'number'),
		);
	}

	/**
	 * Returns properties the user must fill in. Walks the extends chain so
	 * inherited fields (e.g. `clientId`/`clientSecret` from `oAuth2Api`) are
	 * considered, and applies `displayOptions` against the effective defaults
	 * — matching the credential edit modal's `credentialProperties` /
	 * `displayCredentialParameter` logic.
	 */
	function getManuallyConfigurableProperties(credentialType: ICredentialType): INodeProperties[] {
		const mergedProperties = getMergedCredentialProperties(credentialType.name);
		const defaults: ICredentialDataDecryptedObject = {};
		for (const prop of mergedProperties) {
			defaults[prop.name] = prop.default as CredentialInformation;
		}

		return mergedProperties.filter((prop) => {
			if (prop.type === 'hidden' || prop.type === 'notice') return false;
			return NodeHelpers.displayParameter(defaults, prop, null, null);
		});
	}

	function getMergedCredentialProperties(
		credentialTypeName: string,
		visited = new Set<string>(),
	): INodeProperties[] {
		if (visited.has(credentialTypeName)) return [];
		visited.add(credentialTypeName);

		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) return [];
		if (credentialType.extends === undefined) return credentialType.properties;

		const merged: INodeProperties[] = [];
		for (const parentName of credentialType.extends) {
			NodeHelpers.mergeNodeProperties(merged, getMergedCredentialProperties(parentName, visited));
		}
		NodeHelpers.mergeNodeProperties(merged, credentialType.properties);
		return merged;
	}

	function hasManualCredentialInputFields(credentialType: ICredentialType): boolean {
		return getManuallyConfigurableProperties(credentialType).length > 0;
	}

	async function getOAuthAuthorizationUrl(
		credential: ICredentialsResponse,
	): Promise<Result<string, 'api-error' | 'no-url'>> {
		const parentTypes = getParentTypes(credential.type);

		try {
			if (credential.type === 'oAuth2Api' || parentTypes.includes('oAuth2Api')) {
				return createResultOk(await credentialsStore.oAuth2Authorize(credential));
			}
			if (credential.type === 'oAuth1Api' || parentTypes.includes('oAuth1Api')) {
				return createResultOk(await credentialsStore.oAuth1Authorize(credential));
			}
		} catch (error) {
			toast.showError(
				error,
				i18n.baseText('credentialEdit.credentialEdit.showError.generateAuthorizationUrl.title'),
			);
			return createResultError('api-error');
		}

		return createResultError('no-url');
	}

	function isValidHttpUrl(url: string): boolean {
		try {
			const parsed = new URL(url);
			return ['http:', 'https:'].includes(parsed.protocol);
		} catch {
			return false;
		}
	}

	function showOAuthUrlError(): void {
		toast.showError(
			new Error(i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.message')),
			i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.title'),
		);
	}

	function openOAuthPopup(url: string, signal?: AbortSignal): Window | null {
		const params =
			'scrollbars=no,resizable=yes,status=no,titlebar=no,location=no,toolbar=no,menubar=no,width=500,height=700';
		const popup = window.open(url, 'OAuth Authorization', params);

		signal?.addEventListener('abort', () => {
			popup?.close();
		});

		return popup;
	}

	async function waitForOAuthCallback(popup: Window, signal?: AbortSignal): Promise<boolean> {
		return await new Promise((resolve) => {
			const oauthChannel = new BroadcastChannel('oauth-callback');
			const trustedOrigins = getTrustedOAuthOrigins(rootStore.urlBaseEditor);
			let settled = false;

			function settle(result: boolean) {
				if (settled) return;
				settled = true;
				oauthChannel.close();
				clearInterval(popupClosedPoll);
				window.removeEventListener('message', onWindowMessage);
				resolve(result);
			}

			function handleResult(result: boolean) {
				if (settled) return;
				popup.close();

				if (result) {
					toast.showMessage({
						title: i18n.baseText('nodeCredentials.oauth.accountConnected'),
						type: 'success',
					});
				} else {
					toast.showMessage({
						title: i18n.baseText('nodeCredentials.oauth.accountConnectionFailed'),
						type: 'error',
					});
				}

				settle(result);
			}

			// Cross-origin embed fallback: the callback page also posts to the opener.
			function onWindowMessage(event: MessageEvent) {
				const result = parseOAuthCallbackMessage(event, trustedOrigins);
				if (result === null) return;
				handleResult(result === 'success');
			}

			signal?.addEventListener('abort', () => {
				settle(false);
			});

			oauthChannel.addEventListener('message', (event: MessageEvent) => {
				handleResult(event.data === 'success');
			});

			window.addEventListener('message', onWindowMessage);

			// Fallback: if the popup is closed without delivering a callback (e.g. the
			// user closes it manually), no message ever arrives. Poll for the closed
			// popup so the promise resolves and the listeners above are cleaned up
			// instead of leaking and hanging indefinitely.
			const popupClosedPoll = setInterval(() => {
				if (popup.closed) {
					settle(false);
				}
			}, 500);
		});
	}

	/**
	 * Authorize OAuth credentials by opening a popup and listening for callback.
	 * Returns true if OAuth was successful, false if cancelled or failed.
	 */
	async function authorize(
		credential: ICredentialsResponse,
		signal?: AbortSignal,
	): Promise<boolean> {
		const urlResult = await getOAuthAuthorizationUrl(credential);
		if (!urlResult.ok) {
			if (urlResult.error === 'no-url') showOAuthUrlError();
			return false;
		}

		if (!isValidHttpUrl(urlResult.result)) {
			showOAuthUrlError();
			return false;
		}

		const popup = openOAuthPopup(urlResult.result, signal);
		if (!popup) {
			showOAuthUrlError();
			return false;
		}

		return await waitForOAuthCallback(popup, signal);
	}

	/**
	 * Create a new OAuth credential and run the full authorization flow.
	 * Returns the credential on success, null on failure (cleans up automatically).
	 */
	async function createAndAuthorize(
		credentialTypeName: string,
		nodeType?: string,
	): Promise<ICredentialsResponse | null> {
		const credentialType = credentialsStore.getCredentialTypeByName(credentialTypeName);
		if (!credentialType) {
			return null;
		}

		const data: ICredentialDataDecryptedObject = {};
		const allowedHttpRequestDomainsProperty = credentialType.properties.find(
			(prop) => prop.name === 'allowedHttpRequestDomains',
		);
		if (!allowedHttpRequestDomainsProperty || allowedHttpRequestDomainsProperty.type !== 'hidden') {
			data.allowedHttpRequestDomains = 'none';
		}

		let credential: ICredentialsResponse;
		try {
			const name = await credentialsStore.getNewCredentialName({
				credentialTypeName,
				fallbackName: credentialType.displayName,
			});
			credential = await credentialsStore.createNewCredential(
				{
					id: '',
					name,
					type: credentialTypeName,
					data,
				},
				projectsStore.currentProject?.id,
				undefined,
				{ skipStoreUpdate: true },
			);

			telemetry.track('User created credentials', {
				credential_type: credential.type,
				credential_id: credential.id,
				workflow_id: workflowsStore.workflowId,
			});
		} catch (error) {
			toast.showError(error, i18n.baseText('nodeCredentials.showMessage.title'));
			return null;
		}

		const controller = new AbortController();
		oauthAbortController.value = controller;
		pendingCredentialId.value = credential.id;

		const success = await authorize(credential, controller.signal);

		oauthAbortController.value = null;
		pendingCredentialId.value = null;

		const trackProperties: Record<string, GenericValue> = {
			credential_type: credentialTypeName,
			workflow_id: workflowsStore.workflowId ?? null,
			credential_id: credential.id,
			is_complete: true,
			is_new: true,
			is_valid: success,
			uses_external_secrets: false,
		};

		if (nodeType) {
			trackProperties.node_type = nodeType;
		}

		telemetry.track('User saved credentials', trackProperties);

		if (success) {
			credentialsStore.upsertCredential(credential);

			return credential;
		}

		void credentialsStore.deleteCredential({ id: credential.id });
		return null;
	}

	/**
	 * Cancel any in-progress OAuth authorization and clean up the pending credential.
	 */
	function cancelAuthorize() {
		if (oauthAbortController.value) {
			oauthAbortController.value.abort();
		}
		if (pendingCredentialId.value) {
			void credentialsStore.deleteCredential({ id: pendingCredentialId.value });
		}
	}

	return {
		getParentTypes,
		isOAuthCredentialType,
		isGoogleOAuthType,
		canOAuthCredentialQuickConnect,
		hasManualCredentialInputFields,
		authorize,
		createAndAuthorize,
		cancelAuthorize,
	};
}
